import { computed, readonly, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  ruleCycleIds,
  type ConditionalFormRule,
  type ConditionalRuleAction,
  type ConditionalRuleTarget,
  type ConditionalThenBlock,
  type FormRulesStructure,
} from '@record-fill';
import type { ElementType, FormElement, TableCell, TableElement } from './form.types';
import { useLimitedHistory } from './useLimitedHistory';
import {
  ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
  appendRuleOptionUsage,
  getPendingRuleCreationDraftUsageId,
  canAddRuleForTriggerElement,
  type RuleLimitEntry,
  type RuleOptionUsage,
} from '../lib/rule-trigger-options';
import { isRulesSelectableType } from '../lib/rules-field-eligibility';

type RulesScopeType = 'none' | 'single' | 'multi' | 'row' | 'column' | 'table' | 'all';
type TableRuleScopeType = Exclude<RulesScopeType, 'none' | 'single' | 'multi' | 'all'>;

export type {
  ConditionalFormRule,
  ConditionalRuleAction,
  ConditionalRuleTarget,
  ConditionalThenBlock,
  FormRulesStructure,
} from '@record-fill';

type RulesMenuAnchor =
  | { kind: 'none' }
  | { kind: 'element'; elementId: string }
  | { kind: 'table-cell'; tableId: string; row: number; col: number }
  | { kind: 'table-scope'; tableId: string; scope: 'row' | 'column' | 'table'; index?: number }
  | { kind: 'select-all' };

interface RulesSelectedField {
  id: string;
  type: ElementType;
  tableId?: string;
  row?: number;
  col?: number;
}

export const BLANK_RULE_NAME_ERROR = 'Blank conditional rule name';
const RULES_STORAGE_VERSION = 1;

export class RuleCycleError extends Error {
  constructor(readonly cycles: string[][]) {
    super('Conditional rule closes a cycle');
    this.name = 'RuleCycleError';
  }
}

export function isRuleCycleError(error: unknown): error is RuleCycleError {
  return error instanceof RuleCycleError;
}

type ApplyRuleInput = Omit<ConditionalFormRule, 'id' | 'createdAt' | 'thenBlocks'> & {
  thenBlocks?: ConditionalThenBlock[];
};

type UpdateRuleInput = ApplyRuleInput & {
  id: string;
};

type RuleCreationDraftProvider = () => RuleCreationDraft | null;

interface RulesHistorySnapshot {
  rules: ConditionalFormRule[];
  nextRuleNumber: number;
  anchorFieldIds: string[];
  reportFieldIds: string[];
}

interface StoredRulesDraft {
  version: number;
  rules: ConditionalFormRule[];
  nextRuleNumber: number;
  creationDraft?: RuleCreationDraft | null;
  pendingCreationDrafts?: RuleCreationDraft[];
  savedThenItemSelection?: RuleSavedThenItemSelection | null;
  expandedRuleIds?: string[];
  anchorFieldIds?: string[];
  reportFieldIds?: string[];
  configAnchorFieldIds?: string[];
}

interface LoadPersistedRulesOptions {
  rulesStructure?: object | null;
  preferStoredDraft?: boolean;
}

export interface RuleCreationDraftThenBlock extends ConditionalThenBlock {
  confirmed: boolean;
}

export interface RuleCreationDraft {
  triggerFieldId: string;
  ruleName: string;
  triggerOption: string;
  thenBlocks: RuleCreationDraftThenBlock[];
  activeThenBlockId: string | null;
  isExpanded: boolean;
}

export interface RuleSavedThenItemSelection {
  ruleId: string;
  blockId: string;
}

function getRulesStorageKey(branchId: string | null): string | null {
  if (!branchId) return null;
  return `form-builder-rules-draft-branch-${branchId}`;
}

function getEditRulesStorageKey(matrixId: number): string {
  return `form-builder-rules-draft-matrix-${matrixId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isRuleAction(value: unknown): value is ConditionalRuleAction {
  return value === 'show' || value === 'hide' || value === 'disable';
}

function isRuleTarget(value: unknown): value is ConditionalRuleTarget {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    (value.type === 'field' || value.type === 'table') &&
    typeof value.label === 'string' &&
    isStringArray(value.fieldIds)
  );
}

function isThenBlock(value: unknown): value is ConditionalThenBlock {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    (value.action === '' || isRuleAction(value.action)) &&
    Array.isArray(value.targets) &&
    value.targets.every(isRuleTarget)
  );
}

function isConditionalRule(value: unknown): value is ConditionalFormRule {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.triggerFieldId === 'string' &&
    typeof value.triggerOption === 'string' &&
    isRuleAction(value.action) &&
    isStringArray(value.targetFieldIds) &&
    Array.isArray(value.thenBlocks) &&
    value.thenBlocks.every(isThenBlock) &&
    typeof value.createdAt === 'string'
  );
}

function isStoredRulesDraft(value: unknown): value is StoredRulesDraft {
  return (
    isRecord(value) &&
    value.version === RULES_STORAGE_VERSION &&
    Array.isArray(value.rules) &&
    value.rules.every(isConditionalRule) &&
    typeof value.nextRuleNumber === 'number' &&
    (value.creationDraft === undefined ||
      value.creationDraft === null ||
      isRuleCreationDraft(value.creationDraft)) &&
    (value.pendingCreationDrafts === undefined ||
      (Array.isArray(value.pendingCreationDrafts) &&
        value.pendingCreationDrafts.every(isRuleCreationDraft))) &&
    (value.savedThenItemSelection === undefined ||
      value.savedThenItemSelection === null ||
      isRuleSavedThenItemSelection(value.savedThenItemSelection)) &&
    (value.expandedRuleIds === undefined || isStringArray(value.expandedRuleIds)) &&
    (value.anchorFieldIds === undefined || isStringArray(value.anchorFieldIds)) &&
    (value.reportFieldIds === undefined || isStringArray(value.reportFieldIds)) &&
    (value.configAnchorFieldIds === undefined || isStringArray(value.configAnchorFieldIds))
  );
}

function isFormRulesStructure(value: unknown): value is FormRulesStructure {
  return (
    isRecord(value) &&
    value.version === RULES_STORAGE_VERSION &&
    Array.isArray(value.rules) &&
    value.rules.every(isConditionalRule) &&
    typeof value.nextRuleNumber === 'number' &&
    (value.anchorFieldIds === undefined || isStringArray(value.anchorFieldIds)) &&
    (value.reportFieldIds === undefined || isStringArray(value.reportFieldIds)) &&
    (value.configAnchorFieldIds === undefined || isStringArray(value.configAnchorFieldIds))
  );
}

function isRuleCreationDraftThenBlock(value: unknown): value is RuleCreationDraftThenBlock {
  return isThenBlock(value) && isRecord(value) && typeof value.confirmed === 'boolean';
}

function isRuleCreationDraft(value: unknown): value is RuleCreationDraft {
  return (
    isRecord(value) &&
    typeof value.triggerFieldId === 'string' &&
    typeof value.ruleName === 'string' &&
    typeof value.triggerOption === 'string' &&
    Array.isArray(value.thenBlocks) &&
    value.thenBlocks.every(isRuleCreationDraftThenBlock) &&
    (typeof value.activeThenBlockId === 'string' || value.activeThenBlockId === null) &&
    typeof value.isExpanded === 'boolean'
  );
}

function isRuleSavedThenItemSelection(value: unknown): value is RuleSavedThenItemSelection {
  return isRecord(value) && typeof value.ruleId === 'string' && typeof value.blockId === 'string';
}

function isTableElement(element: FormElement): element is TableElement {
  return element.type === 'TABLE';
}

function isSelectableStandaloneElement(element: FormElement): boolean {
  return isRulesSelectableType(element.type);
}

function isTriggerFieldType(type: ElementType): boolean {
  return type === 'CHECKBOX' || type === 'RADIO_GROUP' || type === 'DROPDOWN';
}

function mapCellContentToSelectedField(
  tableId: string,
  cell: TableCell
): RulesSelectedField | null {
  if (!cell.content || !isRulesSelectableType(cell.content.type)) return null;

  return {
    id: cell.content.id,
    type: cell.content.type,
    tableId,
    row: cell.rowIndex,
    col: cell.colIndex,
  };
}

function uniqueFields(fields: RulesSelectedField[]): RulesSelectedField[] {
  const seen = new Set<string>();

  return fields.filter((field) => {
    if (seen.has(field.id)) {
      return false;
    }

    seen.add(field.id);
    return true;
  });
}

function uniqueCells(cells: { row: number; col: number }[]) {
  const seen = new Set<string>();

  return cells.filter((cell) => {
    const key = `${cell.row}:${cell.col}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function uniqueIds(ids: string[]): string[] {
  return Array.from(new Set(ids));
}

function getRuleThenBlocks(rule: ApplyRuleInput): ConditionalThenBlock[] {
  return (
    rule.thenBlocks ?? [
      {
        id: 'then-1',
        action: rule.action,
        targets: rule.targetFieldIds.map((fieldId) => ({
          id: fieldId,
          type: 'field' as const,
          label: fieldId,
          fieldIds: [fieldId],
        })),
      },
    ]
  );
}

function cloneRules(rules: readonly ConditionalFormRule[]): ConditionalFormRule[] {
  return rules.map((rule) => ({
    ...rule,
    targetFieldIds: [...rule.targetFieldIds],
    thenBlocks: rule.thenBlocks.map((block) => ({
      ...block,
      targets: block.targets.map((target) => ({
        ...target,
        fieldIds: [...target.fieldIds],
      })),
    })),
  }));
}

function countThenBlockTargetFields(thenBlocks: ConditionalThenBlock[]): number {
  return thenBlocks.flatMap((block) => block.targets.flatMap((target) => target.fieldIds)).length;
}

function isRuleTargetMissing(
  target: ConditionalRuleTarget,
  fieldIds: Set<string>,
  tableFieldIds: Map<string, string[]>
) {
  if (target.type === 'table') {
    const existingTableFieldIds = tableFieldIds.get(target.id);
    return (
      !existingTableFieldIds ||
      target.fieldIds.some((fieldId) => !existingTableFieldIds.includes(fieldId))
    );
  }

  return !fieldIds.has(target.id) || target.fieldIds.some((fieldId) => !fieldIds.has(fieldId));
}

function ruleHasMissingTarget(
  rule: ConditionalFormRule,
  fieldIds: Set<string>,
  tableFieldIds: Map<string, string[]>
) {
  return rule.thenBlocks.some((block) =>
    block.targets.some((target) => isRuleTargetMissing(target, fieldIds, tableFieldIds))
  );
}

function isRuleAssociatedWithFieldIds(rule: ConditionalFormRule, elementFieldIds: Set<string>) {
  if (elementFieldIds.has(rule.triggerFieldId)) {
    return true;
  }

  return rule.thenBlocks.some((block) =>
    block.targets.some((target) => isTargetAssociatedWithFieldIds(target, elementFieldIds))
  );
}

function isTargetAssociatedWithFieldIds(
  target: ConditionalRuleTarget,
  elementFieldIds: Set<string>
) {
  return (
    elementFieldIds.has(target.id) ||
    target.fieldIds.some((fieldId) => elementFieldIds.has(fieldId))
  );
}

function getScopeTypeForFieldCount(fieldCount: number): RulesScopeType {
  if (fieldCount === 0) return 'none';
  if (fieldCount === 1) return 'single';
  return 'multi';
}

function getElementSelectionAnchor(
  fields: readonly RulesSelectedField[],
  fallbackElementId: string
): RulesMenuAnchor {
  if (fields.length === 1) {
    return { kind: 'element', elementId: fields[0].id };
  }

  return { kind: 'element', elementId: fallbackElementId };
}

function getTableScopeFields(table: TableElement, scope: TableRuleScopeType, index?: number) {
  if (scope === 'table') return collectTableFields(table);
  if (scope === 'row') return collectTableRowFields(table, index ?? 0);
  return collectTableColumnFields(table, index ?? 0);
}

function getTableScopeAnchor(
  tableId: string,
  scope: TableRuleScopeType,
  fields: readonly RulesSelectedField[],
  index?: number
): RulesMenuAnchor {
  if (fields.length === 0) return { kind: 'none' };
  return {
    kind: 'table-scope',
    tableId,
    scope,
    index,
  };
}

function collectExistingRuleTargets(elements: readonly FormElement[]) {
  const fieldIds = new Set<string>();
  const tableFieldIds = new Map<string, string[]>();

  elements.forEach((element) => {
    if (element.type === 'HEADER') return;

    if (element.type !== 'TABLE') {
      fieldIds.add(element.id);
      return;
    }

    const table = element as TableElement;
    const nestedFieldIds = table.properties.cells
      .flatMap((row) => row.map((cell) => cell.content?.id))
      .filter((fieldId): fieldId is string => !!fieldId);

    nestedFieldIds.forEach((fieldId) => fieldIds.add(fieldId));
    tableFieldIds.set(element.id, nestedFieldIds);
  });

  return { fieldIds, tableFieldIds };
}

function collectElementFieldIds(element: FormElement): string[] {
  if (!isTableElement(element)) {
    return [element.id];
  }

  return [
    element.id,
    ...element.properties.cells
      .flatMap((row) => row.map((cell) => cell.content?.id))
      .filter((fieldId): fieldId is string => !!fieldId),
  ];
}

function collectTableFields(table: TableElement): RulesSelectedField[] {
  return uniqueFields(
    table.properties.cells.flatMap((row) =>
      row
        .map((cell) => mapCellContentToSelectedField(table.id, cell))
        .filter((field): field is RulesSelectedField => field !== null)
    )
  );
}

function collectTableRowFields(table: TableElement, rowIndex: number): RulesSelectedField[] {
  return uniqueFields(
    (table.properties.cells[rowIndex] ?? [])
      .map((cell) => mapCellContentToSelectedField(table.id, cell))
      .filter((field): field is RulesSelectedField => field !== null)
  );
}

function collectTableColumnFields(table: TableElement, colIndex: number): RulesSelectedField[] {
  const fields: RulesSelectedField[] = [];

  for (let rowIndex = 0; rowIndex < table.properties.rows; rowIndex += 1) {
    const cell = table.properties.cells[rowIndex]?.[colIndex];
    const field = cell ? mapCellContentToSelectedField(table.id, cell) : null;

    if (field) {
      fields.push(field);
    }
  }

  return uniqueFields(fields);
}

function collectAllFormFields(elements: FormElement[]): RulesSelectedField[] {
  return uniqueFields(
    elements.flatMap((element) => {
      if (isTableElement(element)) {
        return collectTableFields(element);
      }

      if (!isRulesSelectableType(element.type)) {
        return [];
      }

      return [
        {
          id: element.id,
          type: element.type,
        },
      ];
    })
  );
}

function findFormFieldElement(
  elements: readonly FormElement[],
  fieldId: string
): FormElement | null {
  for (const element of elements) {
    if (element.id === fieldId) {
      return element;
    }

    if (!isTableElement(element)) {
      continue;
    }

    const nestedField = element.properties.cells
      .flatMap((row) => row.map((cell) => cell.content))
      .find((field): field is FormElement => field?.id === fieldId);

    if (nestedField) {
      return nestedField;
    }
  }

  return null;
}

export const useFormRulesStore = defineStore('form-rules', () => {
  const currentBranchId = ref<string | null>(localStorage.getItem('selectedBranchId'));
  const editRulesStorageKey = ref<string | null>(null);
  const localStorageSuspended = ref(false);
  const activeTableId = ref<string | null>(null);
  const selectedFields = ref<RulesSelectedField[]>([]);
  const scopeType = ref<RulesScopeType>('none');
  const menuAnchor = ref<RulesMenuAnchor>({ kind: 'none' });
  const rules = ref<ConditionalFormRule[]>([]);
  const currentRuleId = ref<string | null>(null);
  const duplicateRuleNameErrorRuleIds = ref<string[]>([]);
  const ruleCreationDraftError = ref(false);
  const incompleteSavedRuleErrorIds = ref<string[]>([]);
  const draftTriggerFieldId = ref<string | null>(null);
  const draftTargetFieldIds = ref<string[]>([]);
  const draftInteractionLocked = ref(false);
  const ruleItemSelectionActive = ref(false);
  const ruleCreationDraft = ref<RuleCreationDraft | null>(null);
  const pendingCreationDrafts = ref<RuleCreationDraft[]>([]);
  const ruleCreationDraftProvider = ref<RuleCreationDraftProvider | null>(null);
  const ruleCreationDraftUnsavedError = ref(false);
  const ruleCreationSessionId = ref(0);
  const savedThenItemSelection = ref<RuleSavedThenItemSelection | null>(null);
  const expandedRuleIdsDraft = ref<string[]>([]);
  const anchorFieldIds = ref<string[]>([]);
  const reportFieldIds = ref<string[]>([]);
  // Anchors the record configuration added on top of the user's; the builder never edits them,
  // it only carries them so the next configuration save can tell its own anchors apart.
  const configAnchorFieldIds = ref<string[]>([]);
  const nextRuleNumber = ref(1);
  const focusedRuleId = ref<string | null>(null);
  const focusedRuleVersion = ref(0);
  let focusedRuleTimer: ReturnType<typeof setTimeout> | null = null;
  const fieldConfigurations = computed(() => ({
    anchor: new Set(anchorFieldIds.value),
    report: new Set(reportFieldIds.value),
  }));

  const selectedFieldIds = computed(() => selectedFields.value.map((field) => field.id));
  const hasSelection = computed(() => selectedFields.value.length > 0);
  const isCreatingRule = computed(() => draftTriggerFieldId.value !== null);
  const hasPendingRuleCreationDrafts = computed(() => pendingCreationDrafts.value.length > 0);
  const affectedFieldIds = computed(() => {
    const ids = new Set<string>();
    rules.value.forEach((rule) => {
      rule.targetFieldIds.forEach((fieldId) => ids.add(fieldId));
    });
    return Array.from(ids);
  });
  const isAllSelected = computed(() => scopeType.value === 'all' && hasSelection.value);
  const incompleteSavedRuleIds = computed(() =>
    rules.value
      .filter((rule) =>
        rule.thenBlocks.some((block) => !block.action || block.targets.length === 0)
      )
      .map((rule) => rule.id)
  );
  const hasIncompleteSavedRules = computed(() => incompleteSavedRuleIds.value.length > 0);
  const ruleTriggerOptionUsages = computed<RuleOptionUsage[]>(() => {
    const usages: RuleOptionUsage[] = [...rules.value];
    const creationDraft = ruleCreationDraftProvider.value?.() ?? ruleCreationDraft.value;

    if (creationDraft) {
      appendRuleOptionUsage(
        usages,
        ACTIVE_RULE_CREATION_DRAFT_USAGE_ID,
        creationDraft.triggerFieldId,
        creationDraft.triggerOption
      );
    }

    pendingCreationDrafts.value.forEach((draft, index) => {
      appendRuleOptionUsage(
        usages,
        getPendingRuleCreationDraftUsageId(index),
        draft.triggerFieldId,
        draft.triggerOption
      );
    });

    return usages;
  });
  const ruleLimitEntries = computed<RuleLimitEntry[]>(() => {
    const entries: RuleLimitEntry[] = rules.value.map((rule) => ({
      triggerFieldId: rule.triggerFieldId,
      triggerOption: rule.triggerOption,
      isDraft: false,
    }));

    if (draftTriggerFieldId.value) {
      entries.push({ triggerFieldId: draftTriggerFieldId.value, triggerOption: '', isDraft: true });
    }

    pendingCreationDrafts.value.forEach((draft) =>
      entries.push({ triggerFieldId: draft.triggerFieldId, triggerOption: '', isDraft: true })
    );

    return entries;
  });
  const canAddRule = computed(() => {
    if (scopeType.value !== 'single' || selectedFields.value.length !== 1) {
      return false;
    }

    const selectedField = selectedFields.value[0];
    return isTriggerFieldType(selectedField.type);
  });

  function hasAvailableRuleTarget(elements: FormElement[], triggerFieldId: string) {
    return collectAllFormFields(elements).some((field) => field.id !== triggerFieldId);
  }

  function canAddRuleForElement(element: FormElement | null | undefined) {
    return canAddRuleForTriggerElement(
      element,
      ruleLimitEntries.value,
      ruleTriggerOptionUsages.value
    );
  }

  function createHistorySnapshot(): RulesHistorySnapshot {
    return {
      rules: cloneRules(rules.value),
      nextRuleNumber: nextRuleNumber.value,
      anchorFieldIds: [...anchorFieldIds.value],
      reportFieldIds: [...reportFieldIds.value],
    };
  }

  function restoreHistorySnapshot(snapshot: RulesHistorySnapshot) {
    rules.value = cloneRules(snapshot.rules);
    nextRuleNumber.value = snapshot.nextRuleNumber;
    anchorFieldIds.value = [...snapshot.anchorFieldIds];
    reportFieldIds.value = [...snapshot.reportFieldIds];
    draftTriggerFieldId.value = null;
    draftTargetFieldIds.value = [];
    draftInteractionLocked.value = false;
    ruleItemSelectionActive.value = false;
    ruleCreationDraft.value = null;
    pendingCreationDrafts.value = [];
    ruleCreationDraftUnsavedError.value = false;
    incompleteSavedRuleErrorIds.value = [];
    ruleCreationSessionId.value = 0;
    savedThenItemSelection.value = null;
    expandedRuleIdsDraft.value = [];
    activeTableId.value = null;
    selectedFields.value = [];
    scopeType.value = 'none';
    menuAnchor.value = { kind: 'none' };
    persistRules();
  }

  const history = useLimitedHistory<RulesHistorySnapshot>({
    capture: createHistorySnapshot,
    restore: restoreHistorySnapshot,
  });

  const canUndo = history.canUndo;
  const canRedo = history.canRedo;

  function trackRulesChange() {
    history.track();
  }

  function resetTransientState() {
    clearFocusedRule();
    activeTableId.value = null;
    selectedFields.value = [];
    scopeType.value = 'none';
    menuAnchor.value = { kind: 'none' };
    draftTriggerFieldId.value = null;
    draftTargetFieldIds.value = [];
    draftInteractionLocked.value = false;
    ruleItemSelectionActive.value = false;
    ruleCreationDraft.value = null;
    pendingCreationDrafts.value = [];
    ruleCreationDraftUnsavedError.value = false;
    incompleteSavedRuleErrorIds.value = [];
    savedThenItemSelection.value = null;
    expandedRuleIdsDraft.value = [];
    currentRuleId.value = null;
    duplicateRuleNameErrorRuleIds.value = [];
    ruleCreationDraftError.value = false;
    incompleteSavedRuleErrorIds.value = [];
    nextRuleNumber.value = 1;
    configAnchorFieldIds.value = [];
    history.reset();
  }

  function getActiveRulesStorageKey(): string | null {
    return editRulesStorageKey.value ?? getRulesStorageKey(currentBranchId.value);
  }

  function applyRulesStructure(structure: FormRulesStructure) {
    rules.value = cloneRules(structure.rules);
    nextRuleNumber.value = structure.nextRuleNumber;
    anchorFieldIds.value = [...(structure.anchorFieldIds ?? [])];
    reportFieldIds.value = [...(structure.reportFieldIds ?? [])];
    configAnchorFieldIds.value = [...(structure.configAnchorFieldIds ?? [])];
  }

  function applyStoredRulesDraft(draft: StoredRulesDraft) {
    applyRulesStructure(draft);
    ruleCreationDraft.value = draft.creationDraft ?? null;
    pendingCreationDrafts.value = draft.pendingCreationDrafts ?? [];
    draftTriggerFieldId.value = draft.creationDraft?.triggerFieldId ?? null;
    savedThenItemSelection.value = draft.savedThenItemSelection ?? null;
    expandedRuleIdsDraft.value = draft.expandedRuleIds ?? [];
    anchorFieldIds.value = [...(draft.anchorFieldIds ?? [])];
    reportFieldIds.value = [...(draft.reportFieldIds ?? [])];
  }

  function resetRulesStructure() {
    rules.value = [];
    nextRuleNumber.value = 1;
  }

  function getStoredRulesDraft(key: string): StoredRulesDraft | null {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored) as unknown;
      if (isStoredRulesDraft(parsed)) return parsed;
    } catch {
      localStorage.removeItem(key);
      return null;
    }

    localStorage.removeItem(key);
    return null;
  }

  function persistRules() {
    if (localStorageSuspended.value) return;
    const key = getActiveRulesStorageKey();
    if (!key) return;

    const data: StoredRulesDraft = {
      version: RULES_STORAGE_VERSION,
      rules: cloneRules(rules.value),
      nextRuleNumber: nextRuleNumber.value,
      creationDraft: ruleCreationDraft.value,
      pendingCreationDrafts: pendingCreationDrafts.value,
      savedThenItemSelection: savedThenItemSelection.value,
      expandedRuleIds: expandedRuleIdsDraft.value,
      anchorFieldIds: anchorFieldIds.value,
      reportFieldIds: reportFieldIds.value,
      ...(configAnchorFieldIds.value.length > 0 && {
        configAnchorFieldIds: configAnchorFieldIds.value,
      }),
    };
    localStorage.setItem(key, JSON.stringify(data));
  }

  function buildRulesStructure(): FormRulesStructure {
    return {
      version: RULES_STORAGE_VERSION,
      rules: cloneRules(rules.value),
      nextRuleNumber: nextRuleNumber.value,
      anchorFieldIds: [...anchorFieldIds.value],
      reportFieldIds: [...reportFieldIds.value],
      ...(configAnchorFieldIds.value.length > 0 && {
        configAnchorFieldIds: [...configAnchorFieldIds.value],
      }),
    };
  }

  function loadPersistedRules(options: LoadPersistedRulesOptions = {}) {
    const key = getActiveRulesStorageKey();
    resetTransientState();

    if (options.preferStoredDraft && key) {
      const storedDraft = getStoredRulesDraft(key);
      if (storedDraft) {
        applyStoredRulesDraft(storedDraft);
        return;
      }
    }

    if (options.rulesStructure && isFormRulesStructure(options.rulesStructure)) {
      applyRulesStructure(options.rulesStructure);
      return;
    }

    if (!options.preferStoredDraft && key) {
      const storedDraft = getStoredRulesDraft(key);
      if (storedDraft) {
        applyStoredRulesDraft(storedDraft);
        return;
      }

      resetRulesStructure();
      return;
    }

    resetRulesStructure();
  }

  function initForCreateMode() {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    editRulesStorageKey.value = null;
    localStorageSuspended.value = false;
    loadPersistedRules();
  }

  function setEditDraftStorage(matrixId: number) {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    editRulesStorageKey.value = getEditRulesStorageKey(matrixId);
    localStorage.removeItem(editRulesStorageKey.value);
    localStorageSuspended.value = true;
  }

  function clearDraft() {
    const key = getActiveRulesStorageKey();
    if (key) localStorage.removeItem(key);
  }

  function reset() {
    resetTransientState();
    rules.value = [];
    nextRuleNumber.value = 1;
    anchorFieldIds.value = [];
    reportFieldIds.value = [];
  }

  function clearRules(options: { clearFieldConfigurations?: boolean } = {}) {
    if (options.clearFieldConfigurations) {
      clearDraft();
      reset();
      return;
    }

    resetTransientState();
    rules.value = [];
    nextRuleNumber.value = 1;
    persistRules();
  }

  function activateTable(tableId: string) {
    activeTableId.value = tableId;
  }

  function closeMenu() {
    menuAnchor.value = { kind: 'none' };
  }

  function archiveCurrentRuleCreationDraft() {
    const currentDraft = ruleCreationDraftProvider.value?.() ?? ruleCreationDraft.value;
    ruleCreationDraft.value = currentDraft;

    if (!currentDraft) return;

    pendingCreationDrafts.value = [
      ...pendingCreationDrafts.value,
      currentDraft as RuleCreationDraft,
    ];
  }

  function startRuleCreation(elements?: FormElement[]) {
    if (!canAddRule.value) return false;

    const triggerFieldId = selectedFields.value[0].id;
    if (elements && !canAddRuleForElement(findFormFieldElement(elements, triggerFieldId))) {
      closeMenu();
      return false;
    }

    if (elements && !hasAvailableRuleTarget(elements, triggerFieldId)) {
      closeMenu();
      return false;
    }

    archiveCurrentRuleCreationDraft();
    ruleCreationSessionId.value += 1;
    draftTriggerFieldId.value = triggerFieldId;
    draftTargetFieldIds.value = [];
    draftInteractionLocked.value = false;
    ruleItemSelectionActive.value = false;
    ruleCreationDraft.value = null;
    ruleCreationDraftError.value = false;
    ruleCreationDraftUnsavedError.value = false;
    savedThenItemSelection.value = null;
    expandedRuleIdsDraft.value = [];
    closeMenu();
    persistRules();
    return true;
  }

  function cancelRuleCreation() {
    draftTriggerFieldId.value = null;
    draftTargetFieldIds.value = [];
    draftInteractionLocked.value = false;
    ruleItemSelectionActive.value = false;
    ruleCreationDraft.value = null;
    ruleCreationDraftUnsavedError.value = false;
    persistRules();
  }

  function setDraftInteractionLocked(locked: boolean, itemSelectionActive = false) {
    draftInteractionLocked.value = locked;
    ruleItemSelectionActive.value = locked && itemSelectionActive;
  }

  function validateRule(rule: ApplyRuleInput, currentRuleId?: string) {
    const thenBlocks = getRuleThenBlocks(rule);
    const configuredThenBlocks = thenBlocks.filter(
      (block) => block.action && block.targets.length > 0
    );
    const normalizedRuleName = rule.name.trim().toLowerCase();
    const isUpdatingRule = Boolean(currentRuleId);
    const hasRuleIdentity =
      Boolean(normalizedRuleName) &&
      Boolean(rule.triggerFieldId) &&
      Boolean(rule.triggerOption) &&
      Boolean(rule.action);
    const hasValidThenBlockCount = thenBlocks.length > 0 && thenBlocks.length <= 3;
    const hasConfiguredThenBlock = configuredThenBlocks.length > 0;
    const hasConfiguredTargetFields = countThenBlockTargetFields(configuredThenBlocks) > 0;
    const hasIncompleteThenBlock = thenBlocks.some((block) => !block.action);
    const hasDuplicateTriggerOption = rules.value.some(
      (savedRule) =>
        savedRule.triggerFieldId === rule.triggerFieldId &&
        savedRule.triggerOption.trim() === rule.triggerOption.trim() &&
        savedRule.id !== currentRuleId
    );
    const isInvalidNewRule =
      !isUpdatingRule &&
      (!hasConfiguredThenBlock || !hasConfiguredTargetFields || hasIncompleteThenBlock);

    if (!normalizedRuleName) {
      throw new Error(BLANK_RULE_NAME_ERROR);
    }

    if (
      !hasRuleIdentity ||
      !hasValidThenBlockCount ||
      isInvalidNewRule ||
      hasDuplicateTriggerOption
    ) {
      throw new Error('Invalid conditional rule');
    }

    return thenBlocks;
  }

  function assertNoRuleCycle(
    rule: ApplyRuleInput,
    thenBlocks: ConditionalThenBlock[],
    currentRuleId?: string
  ) {
    const candidate: ConditionalFormRule = {
      ...rule,
      id: currentRuleId ?? 'candidate',
      thenBlocks,
      createdAt: '',
    };
    const candidates = [
      ...rules.value.filter((savedRule) => savedRule.id !== currentRuleId),
      candidate,
    ];
    const ownIds = new Set([
      rule.triggerFieldId,
      ...thenBlocks.flatMap((block) => block.targets.flatMap((target) => target.fieldIds)),
      ...rule.targetFieldIds,
    ]);
    const cycles = ruleCycleIds({ ...buildRulesStructure(), rules: candidates }).filter((ids) =>
      ids.some((id) => ownIds.has(id))
    );
    if (cycles.length > 0) {
      throw new RuleCycleError(cycles);
    }
  }

  function applyRule(rule: ApplyRuleInput) {
    const thenBlocks = validateRule(rule);
    assertNoRuleCycle(rule, thenBlocks);
    trackRulesChange();

    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `rule-${Date.now()}`;

    const createdRule = {
      ...rule,
      id,
      name: rule.name.trim(),
      targetFieldIds: uniqueIds(rule.targetFieldIds),
      thenBlocks,
      createdAt: new Date().toISOString(),
    };

    rules.value = [...rules.value, createdRule];
    currentRuleId.value = createdRule.id;
    draftTriggerFieldId.value = null;
    draftTargetFieldIds.value = [];
    ruleCreationDraft.value = null;
    ruleCreationDraftUnsavedError.value = false;
    savedThenItemSelection.value = null;
    activeTableId.value = null;
    selectedFields.value = [];
    scopeType.value = 'none';
    menuAnchor.value = { kind: 'none' };
    nextRuleNumber.value += 1;
    persistRules();

    return createdRule;
  }

  function patchRule(rule: UpdateRuleInput) {
    const savedRule = rules.value.find((item) => item.id === rule.id);
    if (!savedRule) {
      throw new Error('Invalid conditional rule');
    }

    const thenBlocks = validateRule(rule, rule.id);
    assertNoRuleCycle(rule, thenBlocks, rule.id);
    trackRulesChange();

    rules.value = rules.value.map((item) =>
      item.id === rule.id
        ? {
            ...item,
            ...rule,
            id: item.id,
            name: rule.name.trim(),
            targetFieldIds: uniqueIds(rule.targetFieldIds),
            thenBlocks,
            createdAt: item.createdAt,
          }
        : item
    );
    if (!incompleteSavedRuleIds.value.includes(rule.id)) {
      incompleteSavedRuleErrorIds.value = incompleteSavedRuleErrorIds.value.filter(
        (ruleId) => ruleId !== rule.id
      );
    }
    currentRuleId.value = rule.id;
    draftTargetFieldIds.value = uniqueIds(rule.targetFieldIds);
    persistRules();
  }

  function removeRule(ruleId: string) {
    const nextRules = rules.value.filter((rule) => rule.id !== ruleId);
    if (nextRules.length === rules.value.length) return false;

    trackRulesChange();
    rules.value = nextRules;
    if (currentRuleId.value === ruleId) currentRuleId.value = null;
    duplicateRuleNameErrorRuleIds.value = duplicateRuleNameErrorRuleIds.value.filter(
      (duplicateRuleId) => duplicateRuleId !== ruleId
    );
    incompleteSavedRuleErrorIds.value = incompleteSavedRuleErrorIds.value.filter(
      (incompleteRuleId) => incompleteRuleId !== ruleId
    );
    persistRules();
    return true;
  }

  function setCurrentRule(ruleId: string) {
    currentRuleId.value = ruleId;
  }

  function validateUniqueRuleNames() {
    const ruleIdsByName = new Map<string, string[]>();

    for (const rule of rules.value) {
      const normalizedName = rule.name.trim().toLowerCase();
      const ruleIds = ruleIdsByName.get(normalizedName) ?? [];
      ruleIdsByName.set(normalizedName, [...ruleIds, rule.id]);
    }

    duplicateRuleNameErrorRuleIds.value = Array.from(ruleIdsByName.values())
      .filter((ruleIds) => ruleIds.length > 1)
      .flatMap((ruleIds) => ruleIds.slice(1));

    return duplicateRuleNameErrorRuleIds.value.length === 0;
  }

  function validateRuleCreationDraft() {
    ruleCreationDraftError.value =
      isCreatingRule.value ||
      ruleCreationDraft.value !== null ||
      pendingCreationDrafts.value.length > 0;
    ruleCreationDraftUnsavedError.value = ruleCreationDraftError.value;
    return !ruleCreationDraftError.value;
  }

  function validateRulesBeforePersisting() {
    const hasNoIncompleteDraft = validateRuleCreationDraft();
    const hasNoIncompleteSavedRules = !hasIncompleteSavedRules.value;
    if (hasNoIncompleteSavedRules) {
      incompleteSavedRuleErrorIds.value = [];
    } else {
      markRuleCreationDraftAsUnsaved();
    }
    const hasUniqueRuleNames = validateUniqueRuleNames();
    return hasNoIncompleteDraft && hasNoIncompleteSavedRules && hasUniqueRuleNames;
  }

  function hasDuplicateRuleNameError(ruleId: string) {
    return duplicateRuleNameErrorRuleIds.value.includes(ruleId);
  }

  function hasIncompleteSavedRuleError(ruleId: string) {
    return (
      incompleteSavedRuleErrorIds.value.includes(ruleId) &&
      incompleteSavedRuleIds.value.includes(ruleId)
    );
  }

  function reconcileRulesWithElements(elements: readonly FormElement[]) {
    const { fieldIds, tableFieldIds } = collectExistingRuleTargets(elements);
    const nextRules = rules.value.flatMap((rule) => {
      if (!fieldIds.has(rule.triggerFieldId)) {
        return [];
      }

      const hasMissingTarget = ruleHasMissingTarget(rule, fieldIds, tableFieldIds);

      if (hasMissingTarget) {
        return [];
      }

      return [rule];
    });

    if (JSON.stringify(nextRules) === JSON.stringify(rules.value)) {
      return false;
    }

    rules.value = nextRules;
    persistRules();
    return true;
  }

  function undo() {
    return history.undo();
  }

  function redo() {
    return history.redo();
  }

  function hasAppliedRule(fieldId: string) {
    return affectedFieldIds.value.includes(fieldId);
  }

  function hasAppliedTriggerRule(fieldId: string) {
    return rules.value.some((rule) => rule.triggerFieldId === fieldId);
  }

  function hasRuleAssociatedWithElement(element: FormElement) {
    const elementFieldIds = new Set(collectElementFieldIds(element));

    return rules.value.some((rule) => isRuleAssociatedWithFieldIds(rule, elementFieldIds));
  }

  function getTriggerRuleId(element: FormElement) {
    const elementFieldIds = new Set(collectElementFieldIds(element));

    return rules.value.find((rule) => elementFieldIds.has(rule.triggerFieldId))?.id ?? null;
  }

  function focusRule(ruleId: string) {
    if (focusedRuleTimer) {
      clearTimeout(focusedRuleTimer);
    }

    focusedRuleVersion.value += 1;
    focusedRuleId.value = ruleId;
    focusedRuleTimer = setTimeout(() => {
      focusedRuleId.value = null;
      focusedRuleTimer = null;
    }, 2000);
  }

  function clearFocusedRule() {
    if (focusedRuleTimer) {
      clearTimeout(focusedRuleTimer);
      focusedRuleTimer = null;
    }

    focusedRuleId.value = null;
  }

  function setDraftTargetFieldIds(fieldIds: string[]) {
    draftTargetFieldIds.value = uniqueIds(fieldIds);
  }

  function setRuleCreationDraft(draft: RuleCreationDraft | null) {
    ruleCreationDraft.value = draft;
    if (!draft) {
      ruleCreationDraftError.value = false;
      ruleCreationDraftUnsavedError.value = false;
    }
    persistRules();
  }

  function setRuleCreationDraftProvider(provider: RuleCreationDraftProvider | null) {
    ruleCreationDraftProvider.value = provider;
  }

  function removePendingRuleCreationDraft(index: number) {
    if (index < 0 || index >= pendingCreationDrafts.value.length) return;

    pendingCreationDrafts.value = pendingCreationDrafts.value.filter(
      (_, itemIndex) => itemIndex !== index
    );
    persistRules();
  }

  function activatePendingRuleCreationDraft(
    index: number,
    activeThenBlockId: string | null = null
  ) {
    const pendingDraft = pendingCreationDrafts.value[index];
    if (!pendingDraft) return;

    archiveCurrentRuleCreationDraft();
    pendingCreationDrafts.value = pendingCreationDrafts.value.filter(
      (draft) => draft !== pendingDraft
    );
    draftTriggerFieldId.value = pendingDraft.triggerFieldId;
    ruleCreationSessionId.value += 1;
    draftTargetFieldIds.value = pendingDraft.thenBlocks.flatMap((block) =>
      block.targets.flatMap((target) => target.fieldIds)
    );
    draftInteractionLocked.value = !!activeThenBlockId;
    ruleItemSelectionActive.value = !!activeThenBlockId;
    ruleCreationDraft.value = {
      ...pendingDraft,
      activeThenBlockId,
    };
    ruleCreationDraftUnsavedError.value = false;
    savedThenItemSelection.value = null;
    expandedRuleIdsDraft.value = [];
    closeMenu();
    persistRules();
  }

  function updatePendingRuleCreationDraft(index: number, draft: RuleCreationDraft) {
    if (index < 0 || index >= pendingCreationDrafts.value.length) return;

    pendingCreationDrafts.value = pendingCreationDrafts.value.map((currentDraft, itemIndex) =>
      itemIndex === index ? draft : currentDraft
    );
    persistRules();
  }

  function markRuleCreationDraftAsUnsaved() {
    ruleCreationDraftUnsavedError.value = true;
    incompleteSavedRuleErrorIds.value = [...incompleteSavedRuleIds.value];
  }

  function clearRuleCreationDraftUnsavedError() {
    ruleCreationDraftUnsavedError.value = false;
    incompleteSavedRuleErrorIds.value = [];
  }

  function setSavedThenItemSelection(selection: RuleSavedThenItemSelection | null) {
    savedThenItemSelection.value = selection;
    persistRules();
  }

  function setExpandedRuleIdsDraft(ruleIds: string[]) {
    expandedRuleIdsDraft.value = uniqueIds(ruleIds);
    persistRules();
  }

  function isDraftTargetField(fieldId: string) {
    return draftTargetFieldIds.value.includes(fieldId);
  }

  function isDraftTriggerField(fieldId: string) {
    return draftTriggerFieldId.value === fieldId;
  }

  function clearTableScopeSelection(tableId: string) {
    activeTableId.value = tableId;
    selectedFields.value = [];
    scopeType.value = 'none';
    menuAnchor.value = { kind: 'none' };
  }

  function isSingleFieldSelected(fieldId: string) {
    return (
      scopeType.value === 'single' &&
      selectedFields.value.length === 1 &&
      selectedFields.value[0].id === fieldId
    );
  }

  function commitFieldSelection(
    fields: RulesSelectedField[],
    nextScopeType: RulesScopeType,
    nextAnchor: RulesMenuAnchor
  ) {
    const draftTriggerId = draftTriggerFieldId.value;
    const allowedFields =
      draftTriggerId && ruleItemSelectionActive.value
        ? fields.filter((field) => field.id !== draftTriggerId)
        : fields;

    selectedFields.value = uniqueFields(allowedFields);
    scopeType.value = selectedFields.value.length === 0 ? 'none' : nextScopeType;
    menuAnchor.value = selectedFields.value.length === 0 ? { kind: 'none' } : nextAnchor;
  }

  function selectStandaloneField(element: FormElement, additive = false) {
    if (!isSelectableStandaloneElement(element)) {
      return;
    }

    const nextField: RulesSelectedField = {
      id: element.id,
      type: element.type,
    };

    if (!additive || scopeType.value === 'all') {
      activeTableId.value = null;

      if (
        isSingleFieldSelected(element.id) &&
        menuAnchor.value.kind === 'element' &&
        menuAnchor.value.elementId === element.id
      ) {
        clearSelection();
        return;
      }

      commitFieldSelection([nextField], 'single', { kind: 'element', elementId: element.id });
      return;
    }

    const withoutCurrent = selectedFields.value.filter((field) => field.id !== element.id);
    const alreadySelected = withoutCurrent.length !== selectedFields.value.length;
    const nextFields = alreadySelected ? withoutCurrent : [...selectedFields.value, nextField];

    activeTableId.value = null;
    commitFieldSelection(
      nextFields,
      getScopeTypeForFieldCount(nextFields.length),
      getElementSelectionAnchor(nextFields, element.id)
    );
  }

  function selectTableField(table: TableElement, row: number, col: number, additive = false) {
    const cell = table.properties.cells[row]?.[col];
    const field = cell ? mapCellContentToSelectedField(table.id, cell) : null;

    if (!field) {
      return;
    }

    activeTableId.value = table.id;

    if (!additive || scopeType.value === 'all') {
      if (
        isSingleFieldSelected(field.id) &&
        menuAnchor.value.kind === 'table-cell' &&
        menuAnchor.value.tableId === table.id &&
        menuAnchor.value.row === row &&
        menuAnchor.value.col === col
      ) {
        clearTableScopeSelection(table.id);
        return;
      }

      commitFieldSelection([field], 'single', {
        kind: 'table-cell',
        tableId: table.id,
        row,
        col,
      });
      return;
    }

    const withoutCurrent = selectedFields.value.filter(
      (selectedField) => selectedField.id !== field.id
    );
    const alreadySelected = withoutCurrent.length !== selectedFields.value.length;
    const nextFields = alreadySelected ? withoutCurrent : [...selectedFields.value, field];

    commitFieldSelection(nextFields, getScopeTypeForFieldCount(nextFields.length), {
      kind: 'table-cell',
      tableId: table.id,
      row,
      col,
    });
  }

  function selectTableScope(table: TableElement, scope: TableRuleScopeType, index?: number) {
    activeTableId.value = table.id;

    if (
      menuAnchor.value.kind === 'table-scope' &&
      menuAnchor.value.tableId === table.id &&
      menuAnchor.value.scope === scope &&
      menuAnchor.value.index === index
    ) {
      clearTableScopeSelection(table.id);
      return;
    }

    const fields = getTableScopeFields(table, scope, index);

    commitFieldSelection(fields, scope, getTableScopeAnchor(table.id, scope, fields, index));
  }

  function selectAll(elements: FormElement[]) {
    activeTableId.value = null;
    commitFieldSelection(collectAllFormFields(elements), 'all', { kind: 'select-all' });
  }

  function clearSelection() {
    activeTableId.value = null;
    selectedFields.value = [];
    scopeType.value = 'none';
    menuAnchor.value = { kind: 'none' };
  }

  function isFieldSelected(elementId: string) {
    return selectedFields.value.some((field) => field.id === elementId);
  }

  function getSelectedCellsForTable(tableId: string) {
    return uniqueCells(
      selectedFields.value
        .filter(
          (field) => field.tableId === tableId && field.row !== undefined && field.col !== undefined
        )
        .map((field) => ({ row: field.row as number, col: field.col as number }))
    );
  }

  function isCellAnchor(tableId: string, row: number, col: number) {
    return (
      menuAnchor.value.kind === 'table-cell' &&
      menuAnchor.value.tableId === tableId &&
      menuAnchor.value.row === row &&
      menuAnchor.value.col === col
    );
  }

  function isElementAnchor(elementId: string) {
    return menuAnchor.value.kind === 'element' && menuAnchor.value.elementId === elementId;
  }

  function configureSelectedFields(configuration: 'anchor' | 'report') {
    if (selectedFields.value.length === 0) return false;

    const selectedIds = selectedFields.value.map((field) => field.id);

    trackRulesChange();
    const target = configuration === 'anchor' ? anchorFieldIds : reportFieldIds;
    target.value = Array.from(new Set([...target.value, ...selectedIds]));
    persistRules();
    return true;
  }

  function clearSelectedFields(configuration: 'anchor' | 'report' | 'all' = 'all') {
    if (selectedFields.value.length === 0) return false;

    const selectedIds = new Set(selectedFields.value.map((field) => field.id));
    const previousAnchorFieldIds = [...anchorFieldIds.value];
    const previousReportFieldIds = [...reportFieldIds.value];

    trackRulesChange();

    try {
      if (configuration === 'anchor' || configuration === 'all') {
        anchorFieldIds.value = anchorFieldIds.value.filter((fieldId) => !selectedIds.has(fieldId));
      }

      if (configuration === 'report' || configuration === 'all') {
        reportFieldIds.value = reportFieldIds.value.filter((fieldId) => !selectedIds.has(fieldId));
      }

      persistRules();
      return true;
    } catch (error) {
      anchorFieldIds.value = previousAnchorFieldIds;
      reportFieldIds.value = previousReportFieldIds;
      throw error;
    }
  }

  function isFieldAnchorConfigured(fieldId: string) {
    return anchorFieldIds.value.includes(fieldId);
  }

  function isFieldReportConfigured(fieldId: string) {
    return reportFieldIds.value.includes(fieldId);
  }

  return {
    activeTableId: readonly(activeTableId),
    selectedFields: readonly(selectedFields),
    selectedFieldIds,
    rules: readonly(rules),
    duplicateRuleNameErrorRuleIds,
    ruleCreationDraftError,
    draftTriggerFieldId: readonly(draftTriggerFieldId),
    draftTargetFieldIds: readonly(draftTargetFieldIds),
    draftInteractionLocked: readonly(draftInteractionLocked),
    ruleItemSelectionActive: readonly(ruleItemSelectionActive),
    ruleCreationDraft: readonly(ruleCreationDraft),
    pendingCreationDrafts: readonly(pendingCreationDrafts),
    ruleCreationDraftUnsavedError: readonly(ruleCreationDraftUnsavedError),
    ruleCreationSessionId: readonly(ruleCreationSessionId),
    savedThenItemSelection: readonly(savedThenItemSelection),
    expandedRuleIdsDraft: readonly(expandedRuleIdsDraft),
    nextRuleNumber: readonly(nextRuleNumber),
    scopeType: readonly(scopeType),
    menuAnchor: readonly(menuAnchor),
    anchorFieldIds: readonly(anchorFieldIds),
    reportFieldIds: readonly(reportFieldIds),
    fieldConfigurations,
    focusedRuleId: readonly(focusedRuleId),
    focusedRuleVersion: readonly(focusedRuleVersion),
    hasSelection,
    isCreatingRule,
    hasPendingRuleCreationDrafts,
    hasIncompleteSavedRules,
    affectedFieldIds,
    isAllSelected,
    ruleTriggerOptionUsages,
    canAddRule,
    canUndo,
    canRedo,
    reset,
    buildRulesStructure,
    initForCreateMode,
    setEditDraftStorage,
    clearDraft,
    loadPersistedRules,
    clearRules,
    activateTable,
    closeMenu,
    startRuleCreation,
    canAddRuleForElement,
    cancelRuleCreation,
    setDraftInteractionLocked,
    applyRule,
    patchRule,
    removeRule,
    setCurrentRule,
    validateUniqueRuleNames,
    validateRulesBeforePersisting,
    hasDuplicateRuleNameError,
    hasIncompleteSavedRuleError,
    reconcileRulesWithElements,
    undo,
    redo,
    hasAppliedRule,
    hasAppliedTriggerRule,
    hasRuleAssociatedWithElement,
    getTriggerRuleId,
    focusRule,
    clearFocusedRule,
    setDraftTargetFieldIds,
    setRuleCreationDraft,
    setRuleCreationDraftProvider,
    removePendingRuleCreationDraft,
    updatePendingRuleCreationDraft,
    activatePendingRuleCreationDraft,
    markRuleCreationDraftAsUnsaved,
    clearRuleCreationDraftUnsavedError,
    setSavedThenItemSelection,
    setExpandedRuleIdsDraft,
    isDraftTargetField,
    isDraftTriggerField,
    selectStandaloneField,
    selectTableField,
    selectTableScope,
    selectAll,
    clearSelection,
    isFieldSelected,
    getSelectedCellsForTable,
    isCellAnchor,
    isElementAnchor,
    configureSelectedFields,
    clearSelectedFields,
    isFieldAnchorConfigured,
    isFieldReportConfigured,
  };
});
